"use client";

import { createContext, useEffect, useMemo } from "react";
import type { ReactNode } from "react";

type CustomI18nContextType = {
  translations: Record<string, string>;
  ns: string;
  locale: string;
};

export const CustomI18nContext = createContext<CustomI18nContextType | null>(null);

const RTL_LOCALES = new Set(["he", "ar", "fa", "ur"]);

export function CustomI18nProvider({
  children,
  translations,
  locale,
  ns,
}: CustomI18nContextType & {
  children: ReactNode;
}) {
  // Force the document direction + lang to the provided (page-owner's) locale, so a public
  // booking page renders RTL/Hebrew regardless of the visitor's browser language.
  useEffect(() => {
    const d = RTL_LOCALES.has(locale.split("-")[0]) ? "rtl" : "ltr";
    document.documentElement.setAttribute("dir", d);
    document.documentElement.setAttribute("lang", locale);
  }, [locale]);

  // Memoize the value to prevent re-renders unless the data changes
  const value = useMemo(
    () => ({
      translations,
      locale,
      ns,
    }),
    [locale, ns]
  );

  return <CustomI18nContext.Provider value={value}>{children}</CustomI18nContext.Provider>;
}
